import { EnvironmentControls } from './EnvironmentControls.js';
import { CameraControls } from './CameraControls.js';
import { MobileControls } from './MobileControls.js';
import { InfoModalControls } from './InfoModalControls.js';
import { svgIcon, planetIcon } from './icons.js';

/**
 * UIController - Manages user interface controls for the solar system simulation
 * Handles speed controls, trail toggles, and simulation state indicators
 */
export class UIController {
    constructor() {
        this.simulationEngine = null;
        this.renderer3D = null;
        this.isInitialized = false;
        this.updateIntervalId = null; // id del intervalo de actualizaciones periódicas (para poder limpiarlo)

        // Controladores de UI por dominio (extraídos para aligerar este orquestador)
        this.environment = new EnvironmentControls();
        this.camera = new CameraControls();
        this.mobile = new MobileControls(this);
        this.infoModal = new InfoModalControls();

        // UI state
        this.trailsVisible = true;
        this.currentSpeed = 0.1;
        this.isPaused = false;
        this.uiVisible = false; // Start with UI closed

        // Mobile detection
        this.isMobile = this.detectMobile();

        // Listen for window resize to update mobile state
        window.addEventListener('resize', () => {
            const wasMobile = this.isMobile;
            this.isMobile = this.detectMobile();

            // Create or remove mobile controls based on screen size change
            if (this.isMobile && !wasMobile) {
                this.createMobileSpeedControls();
            } else if (!this.isMobile && wasMobile) {
                this.removeMobileSpeedControls();
            }
        });

        // Speed presets
        this.speedPresets = [
            { label: 'Pausado', value: 0 },
            { label: 'Muy Lento', value: 0.1 },
            { label: 'Lento', value: 0.5 },
            { label: 'Normal', value: 1 },
            { label: 'Rápido', value: 5 },
            { label: 'Muy Rápido', value: 20 },
            { label: 'Ultra Rápido', value: 100 },
        ];
    }

    /**
     * Initialize the UI controller with simulation references
     * @param {SimulationEngine} simulationEngine - The simulation engine instance
     * @param {Renderer3D} renderer3D - The 3D renderer instance
     */
    initialize(simulationEngine, renderer3D) {
        this.simulationEngine = simulationEngine;
        this.renderer3D = renderer3D;
        this.environment.setRenderer3D(renderer3D);
        this.camera.setRenderer3D(renderer3D);

        this.createControlElements();
        this.setupEventListeners();
        this.initializeSizingModeUI();

        // Set initial speed to match UI default (Muy Lento = 0.1)
        this.setSpeed(1); // Index 1 = "Muy Lento" with value 0.1

        this.updateUI();

        // Create mobile interface if needed
        if (this.isMobile) {
            this.createMobileSpeedControls();
        }

        this.isInitialized = true;
        console.log('UI Controller initialized successfully');
    }

    /**
     * Create all control elements in the DOM
     */
    createControlElements() {
        const controlsContainer = document.getElementById('controls');
        if (!controlsContainer) {
            console.error('Controls container not found');
            return;
        }

        controlsContainer.innerHTML = `
            <div class="control-section">
                <h4>Controles de Simulación</h4>
                
                <!-- Play/Pause Controls -->
                <div class="control-group">
                    <button id="play-pause-btn" class="control-btn primary">
                        <span class="btn-icon">${svgIcon('pause')}</span>
                        <span class="btn-text">Pausar</span>
                    </button>
                    <button id="reset-btn" class="control-btn secondary">
                        <span class="btn-icon">${svgIcon('reset')}</span>
                        <span class="btn-text">Reiniciar</span>
                    </button>
                </div>

                <!-- Simulation Status -->
                <div class="control-group">
                    <div class="status-display">
                        <div class="status-item">
                            <span class="status-label">Estado:</span>
                            <span id="simulation-status" class="status-value running">Ejecutándose</span>
                        </div>
                        <div class="status-item">
                            <span class="status-label">Tiempo:</span>
                            <span id="simulation-time" class="status-value">0 días</span>
                        </div>
                        <div class="status-item">
                            <span class="status-label">FPS:</span>
                            <span id="fps-display" class="status-value">60</span>
                        </div>
                    </div>
                </div>

                <!-- Speed Controls -->
                <div class="control-group">
                    <label for="speed-slider">Velocidad de Simulación:</label>
                    <div class="speed-controls">
                        <input type="range" id="speed-slider" min="0" max="6" step="1" value="2" class="slider">
                        <span id="speed-display" class="speed-value">Lento (0.5 años/s)</span>
                    </div>
                    <div class="speed-buttons">
                        <button id="speed-down" class="speed-btn">-</button>
                        <button id="speed-up" class="speed-btn">+</button>
                    </div>
                </div>

                <!-- Trail Controls -->
                <div class="control-group separator">
                    <div class="toggle-group">
                        <label class="toggle-switch">
                            <input type="checkbox" id="trails-toggle" checked>
                            <span class="toggle-slider"></span>
                        </label>
                        <label for="trails-toggle" class="toggle-label">Mostrar Estelas</label>
                    </div>
                    
                    <!-- Trail Type Selection -->
                    <div class="trail-type-controls">
                        <label for="trail-type-select">Tipo de Estela:</label>
                        <select id="trail-type-select" class="trail-type-selector">
                            <option value="comet_inverted">🌠 Cometa Invertido</option>
                            <option value="comet">☄️ Cometa</option>
                            <option value="line">━ Línea</option>
                        </select>
                    </div>
                    
                    <button id="clear-trails-btn" class="control-btn secondary small">
                        <span class="btn-icon">${svgIcon('trash')}</span>
                        <span class="btn-text">Limpiar Estelas</span>
                    </button>
                </div>

                <!-- Star Field Controls -->
                <div class="control-group">
                    <h5>Fondo Estelar</h5>
                    <div class="toggle-group">
                        <label class="toggle-switch">
                            <input type="checkbox" id="starfield-toggle" checked>
                            <span class="toggle-slider"></span>
                        </label>
                        <label for="starfield-toggle" class="toggle-label">Mostrar Estrellas</label>
                    </div>
                    <div class="toggle-group">
                        <label class="toggle-switch">
                            <input type="checkbox" id="star-twinkling-toggle">
                            <span class="toggle-slider"></span>
                        </label>
                        <label for="star-twinkling-toggle" class="toggle-label">Efecto Centelleo</label>
                    </div>
                    <div class="toggle-group">
                        <label class="toggle-switch">
                            <input type="checkbox" id="galactic-structure-toggle" checked>
                            <span class="toggle-slider"></span>
                        </label>
                        <label for="galactic-structure-toggle" class="toggle-label">Estructura de Vía Láctea</label>
                    </div>
                    
                    <button id="scientific-info-btn" class="control-btn primary" style="margin-top: 15px; width: 100%; background: #2196F3; font-size: 14px; padding: 12px;">
                        <span class="btn-icon">${svgIcon('book')}</span>
                        <span class="btn-text">Información Científica</span>
                    </button>
                    
                    <!-- Trail Length Controls -->
                    <div class="trail-length-controls">
                        <label for="trail-length-mode">Modo de Longitud:</label>
                        <select id="trail-length-mode" class="trail-mode-selector">
                            <option value="fixed">Fijo</option>
                            <option value="proportional">Proporcional a Órbita</option>
                        </select>
                        
                        <label for="trail-length-slider">Longitud de Estelas:</label>
                        <div class="trail-length-controls-group">
                            <input type="range" id="trail-length-slider" min="0.1" max="3.0" step="0.1" value="1.0" class="slider">
                            <span id="trail-length-display" class="trail-length-value">1.0x</span>
                        </div>
                    </div>

                </div>

                <!-- Solar System Structures Controls -->
                <div class="control-group separator">
                    <h5>🌌 Estructuras del Sistema Solar</h5>
                    <div class="toggle-group">
                        <label class="toggle-switch">
                            <input type="checkbox" id="asteroid-belt-toggle">
                            <span class="toggle-slider"></span>
                        </label>
                        <label for="asteroid-belt-toggle" class="toggle-label">Cinturón de Asteroides</label>
                    </div>
                    <div class="toggle-group">
                        <label class="toggle-switch">
                            <input type="checkbox" id="kuiper-belt-toggle">
                            <span class="toggle-slider"></span>
                        </label>
                        <label for="kuiper-belt-toggle" class="toggle-label">Cinturón de Kuiper</label>
                    </div>
                    <div class="toggle-group">
                        <label class="toggle-switch">
                            <input type="checkbox" id="heliosphere-toggle">
                            <span class="toggle-slider"></span>
                        </label>
                        <label for="heliosphere-toggle" class="toggle-label">Heliosfera</label>
                    </div>
                </div>

                <!-- Camera Presets -->
                <div class="control-group separator">
                    <h5>Vistas de Cámara</h5>
                    <div class="camera-presets">
                        <button class="control-btn secondary small camera-preset-btn" data-preset="top">
                            <span class="btn-icon">${svgIcon('target')}</span>
                            <span class="btn-text">Cenital</span>
                        </button>
                        <button class="control-btn secondary small camera-preset-btn" data-preset="front">
                            <span class="btn-icon">${svgIcon('edgeOn')}</span>
                            <span class="btn-text">De canto</span>
                        </button>
                        <button class="control-btn secondary small camera-preset-btn" data-preset="earth45">
                            <span class="btn-icon">${svgIcon('globe')}</span>
                            <span class="btn-text">Tierra 45°</span>
                        </button>
                        <button class="control-btn secondary small camera-preset-btn" data-preset="overview">
                            <span class="btn-icon">${svgIcon('maximize')}</span>
                            <span class="btn-text">General</span>
                        </button>
                        <button class="control-btn secondary small camera-preset-btn" data-preset="heliosphere">
                            <span class="btn-icon">${svgIcon('heliosphere')}</span>
                            <span class="btn-text">Heliosfera</span>
                        </button>
                    </div>
                </div>

                <!-- Camera Tracking Controls -->
                <div class="control-group separator">
                    <h5>Seguimiento de Cámara</h5>
                    <div class="toggle-group">
                        <label class="toggle-switch">
                            <input type="checkbox" id="tracking-toggle" checked>
                            <span class="toggle-slider"></span>
                        </label>
                        <label for="tracking-toggle" class="toggle-label">Seguimiento Automático</label>
                    </div>
                    <div class="tracking-controls">
                        <button id="track-sun-btn" class="control-btn secondary small active">
                            <span class="btn-icon">${svgIcon('sun')}</span>
                            <span class="btn-text">Sol</span>
                        </button>
                        <button id="pov-mode-btn" class="control-btn secondary small">
                            <span class="btn-icon">${svgIcon('eye')}</span>
                            <span class="btn-text">POV</span>
                        </button>
                        <div class="planet-tracking-buttons">
                            <button id="track-mercury-btn" class="control-btn secondary small planet-btn" data-planet="mercury" aria-label="Seguir a Mercurio" title="Mercurio">
                                <span class="btn-icon">${planetIcon('#8C7853')}</span>
                                <span class="planet-label">Mercurio</span>
                            </button>
                            <button id="track-venus-btn" class="control-btn secondary small planet-btn" data-planet="venus" aria-label="Seguir a Venus" title="Venus">
                                <span class="btn-icon">${planetIcon('#FFC649')}</span>
                                <span class="planet-label">Venus</span>
                            </button>
                            <button id="track-earth-btn" class="control-btn secondary small planet-btn" data-planet="earth" aria-label="Seguir a la Tierra" title="Tierra">
                                <span class="btn-icon">${planetIcon('#6B93D6')}</span>
                                <span class="planet-label">Tierra</span>
                            </button>
                            <button id="track-mars-btn" class="control-btn secondary small planet-btn" data-planet="mars" aria-label="Seguir a Marte" title="Marte">
                                <span class="btn-icon">${planetIcon('#CD5C5C')}</span>
                                <span class="planet-label">Marte</span>
                            </button>
                            <button id="track-jupiter-btn" class="control-btn secondary small planet-btn" data-planet="jupiter" aria-label="Seguir a Júpiter" title="Júpiter">
                                <span class="btn-icon">${planetIcon('#D8CA9D')}</span>
                                <span class="planet-label">Júpiter</span>
                            </button>
                            <button id="track-saturn-btn" class="control-btn secondary small planet-btn" data-planet="saturn" aria-label="Seguir a Saturno" title="Saturno">
                                <span class="btn-icon">${planetIcon('#FAD5A5', true)}</span>
                                <span class="planet-label">Saturno</span>
                            </button>
                            <button id="track-uranus-btn" class="control-btn secondary small planet-btn" data-planet="uranus" aria-label="Seguir a Urano" title="Urano">
                                <span class="btn-icon">${planetIcon('#4FD0E7')}</span>
                                <span class="planet-label">Urano</span>
                            </button>
                            <button id="track-neptune-btn" class="control-btn secondary small planet-btn" data-planet="neptune" aria-label="Seguir a Neptuno" title="Neptuno">
                                <span class="btn-icon">${planetIcon('#4B70DD')}</span>
                                <span class="planet-label">Neptuno</span>
                            </button>
                        </div>
                        <div class="status-item">
                            <span class="status-label">Siguiendo:</span>
                            <span id="tracking-target" class="status-value">Sol</span>
                        </div>
                        <div class="tracking-distance-control">
                            <label for="tracking-distance-slider">Distancia de Cámara:</label>
                            <div class="distance-controls">
                                <input type="range" id="tracking-distance-slider" min="10" max="2000" step="5" value="1800" class="slider">
                                <span id="distance-display" class="distance-value">1800</span>
                            </div>
                        </div>
                        
                        <!-- POV Mode Toggle -->
                        <div class="pov-mode-control">
                            <div class="toggle-group">
                                <label class="toggle-switch">
                                    <input type="checkbox" id="pov-mode-toggle">
                                    <span class="toggle-slider"></span>
                                </label>
                                <label for="pov-mode-toggle" class="toggle-label">Visión POV</label>
                            </div>
                            <small class="pov-hint">Ver desde el planeta seleccionado</small>
                        </div>
                        
                    </div>
                </div>

                <!-- Realistic Proportions Info -->
                <div class="control-group">
                    <h5>🔬 Proporciones Reales</h5>
                    <div class="status-display">
                        <div class="status-item">
                            <span class="status-label">Tamaños - Sol/Tierra:</span>
                            <span id="size-ratio-display" class="status-value fps-good">109.2x</span>
                        </div>
                        <div class="status-item">
                            <span class="status-label">Tamaños - Sol/Júpiter:</span>
                            <span id="jupiter-ratio-display" class="status-value fps-good">10.0x</span>
                        </div>
                        <div class="status-item">
                            <span class="status-label">Órbitas - Neptuno/Tierra:</span>
                            <span id="neptune-orbit-display" class="status-value fps-good">30.2x</span>
                        </div>
                        <div class="status-item">
                            <span class="status-label">Órbitas - Júpiter/Tierra:</span>
                            <span id="jupiter-orbit-display" class="status-value fps-good">5.2x</span>
                        </div>
                    </div>
                    <div class="keyboard-hint">
                        ✅ Tamaños y órbitas astronómicamente reales<br>
                        📏 Basado en datos NASA/JPL 2025
                    </div>
                </div>

            </div>
        `;
    }

    /**
     * Setup event listeners for all controls
     */
    setupEventListeners() {
        // Play/Pause button
        const playPauseBtn = document.getElementById('play-pause-btn');
        playPauseBtn?.addEventListener('click', () => this.togglePlayPause());

        // Reset button
        const resetBtn = document.getElementById('reset-btn');
        resetBtn?.addEventListener('click', () => this.resetSimulation());

        // Speed slider
        const speedSlider = document.getElementById('speed-slider');
        speedSlider?.addEventListener('input', (e) => this.setSpeed(parseInt(e.target.value)));

        // Speed buttons
        const speedDownBtn = document.getElementById('speed-down');
        const speedUpBtn = document.getElementById('speed-up');
        speedDownBtn?.addEventListener('click', () => this.adjustSpeed(-1));
        speedUpBtn?.addEventListener('click', () => this.adjustSpeed(1));

        // Trails toggle
        const trailsToggle = document.getElementById('trails-toggle');
        trailsToggle?.addEventListener('change', (e) => this.toggleTrails(e.target.checked));

        // Clear trails button
        const clearTrailsBtn = document.getElementById('clear-trails-btn');
        clearTrailsBtn?.addEventListener('click', () => this.clearTrails());

        // Trail type selector
        const trailTypeSelect = document.getElementById('trail-type-select');
        trailTypeSelect?.addEventListener('change', (e) => this.setTrailType(e.target.value));

        // Trail length mode selector
        const trailLengthMode = document.getElementById('trail-length-mode');
        trailLengthMode?.addEventListener('change', (e) => this.setTrailLengthMode(e.target.value));

        // Trail length slider
        const trailLengthSlider = document.getElementById('trail-length-slider');
        trailLengthSlider?.addEventListener('input', (e) =>
            this.setTrailLengthMultiplier(parseFloat(e.target.value))
        );

        // POV mode toggle
        const povModeToggle = document.getElementById('pov-mode-toggle');
        povModeToggle?.addEventListener('change', (e) => this.togglePOVMode(e.target.checked));

        // POV mode button
        const povModeBtn = document.getElementById('pov-mode-btn');
        povModeBtn?.addEventListener('click', () => {
            const povToggle = document.getElementById('pov-mode-toggle');
            if (povToggle) {
                povToggle.checked = !povToggle.checked;
                this.togglePOVMode(povToggle.checked);
            }
        });

        // Star field toggles
        const starfieldToggle = document.getElementById('starfield-toggle');
        starfieldToggle?.addEventListener('change', (e) => this.toggleStarField(e.target.checked));

        const starTwinklingToggle = document.getElementById('star-twinkling-toggle');
        starTwinklingToggle?.addEventListener('change', (e) =>
            this.toggleStarTwinkling(e.target.checked)
        );

        const galacticStructureToggle = document.getElementById('galactic-structure-toggle');
        galacticStructureToggle?.addEventListener('change', (e) =>
            this.toggleGalacticStructure(e.target.checked)
        );

        const scientificInfoBtn = document.getElementById('scientific-info-btn');
        if (scientificInfoBtn) {
            scientificInfoBtn.addEventListener('click', () => this.showScientificInfo());
            console.log('Scientific info button event listener configured');
        } else {
            console.error('Scientific info button not found in DOM');
        }

        // Solar system structures toggles
        const asteroidBeltToggle = document.getElementById('asteroid-belt-toggle');
        asteroidBeltToggle?.addEventListener('change', (e) =>
            this.toggleAsteroidBelt(e.target.checked)
        );

        const kuiperBeltToggle = document.getElementById('kuiper-belt-toggle');
        kuiperBeltToggle?.addEventListener('change', (e) =>
            this.toggleKuiperBelt(e.target.checked)
        );

        const heliosphereToggle = document.getElementById('heliosphere-toggle');
        heliosphereToggle?.addEventListener('change', (e) =>
            this.toggleHeliosphere(e.target.checked)
        );

        // No toggle needed - always realistic proportions

        // Camera preset buttons (vistas predefinidas)
        const presetButtons = document.querySelectorAll('.camera-preset-btn');
        presetButtons.forEach((button) => {
            button.addEventListener('click', () => {
                this.applyCameraPreset(button.getAttribute('data-preset'));
            });
        });

        // Camera tracking controls
        const trackingToggle = document.getElementById('tracking-toggle');
        trackingToggle?.addEventListener('change', (e) =>
            this.toggleCameraTracking(e.target.checked)
        );

        const trackSunBtn = document.getElementById('track-sun-btn');
        trackSunBtn?.addEventListener('click', () => this.trackSun());

        // Planet tracking buttons
        const planetButtons = document.querySelectorAll('.planet-btn');
        planetButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const planetId = button.getAttribute('data-planet');
                this.trackPlanet(planetId);
            });
        });

        // Tracking distance slider
        const distanceSlider = document.getElementById('tracking-distance-slider');
        distanceSlider?.addEventListener('input', (e) =>
            this.setTrackingDistance(parseInt(e.target.value))
        );

        // UI Toggle controls
        this.setupUIToggleControls();

        // Mouse wheel control for camera distance
        this.setupMouseWheelControl();

        // Keyboard shortcuts
        document.addEventListener('keydown', (e) => this.handleKeyboardShortcuts(e));
    }

    /**
     * Handle keyboard shortcuts
     * @param {KeyboardEvent} event - The keyboard event
     */
    handleKeyboardShortcuts(event) {
        // Prevent shortcuts when typing in input fields
        if (event.target.tagName === 'INPUT') return;

        switch (event.code) {
            case 'Space':
                event.preventDefault();
                this.togglePlayPause();
                break;
            case 'KeyR':
                if (event.ctrlKey || event.metaKey) {
                    event.preventDefault();
                    this.resetSimulation();
                }
                break;
            case 'KeyT':
                this.toggleTrails();
                break;
            case 'KeyF': {
                // Toggle camera tracking
                const trackingToggle = document.getElementById('tracking-toggle');
                if (trackingToggle) {
                    trackingToggle.checked = !trackingToggle.checked;
                    this.toggleCameraTracking(trackingToggle.checked);
                }
                break;
            }
            case 'KeyS':
                // Track sun
                this.trackSun();
                break;
            case 'Digit1':
                this.trackPlanet('mercury');
                break;
            case 'Digit2':
                this.trackPlanet('venus');
                break;
            case 'Digit3':
                this.trackPlanet('earth');
                break;
            case 'Digit4':
                this.trackPlanet('mars');
                break;
            case 'Digit5':
                this.trackPlanet('jupiter');
                break;
            case 'Digit6':
                this.trackPlanet('saturn');
                break;
            case 'Digit7':
                this.trackPlanet('uranus');
                break;
            case 'Digit8':
                this.trackPlanet('neptune');
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.adjustSpeed(1);
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.adjustSpeed(-1);
                break;
            case 'KeyH':
                // Toggle UI visibility
                event.preventDefault();
                this.toggleUIVisibility();
                break;
            case 'KeyM':
                // Toggle scale meter
                event.preventDefault();
                if (this.renderer3D && typeof this.renderer3D.toggleScaleMeter === 'function') {
                    this.renderer3D.toggleScaleMeter();
                }
                break;
            case 'KeyL':
                // Toggle trail length mode
                event.preventDefault();
                if (this.renderer3D) {
                    const trailSystem = this.renderer3D.getTrailSystem();
                    if (trailSystem) {
                        const currentMode = trailSystem.getTrailLengthMode();
                        const newMode = currentMode === 'fixed' ? 'proportional' : 'fixed';
                        this.setTrailLengthMode(newMode);
                    }
                }
                break;
            case 'KeyP':
                // Toggle POV mode
                event.preventDefault();
                if (this.renderer3D) {
                    const cameraController = this.renderer3D.getCameraController();
                    if (cameraController) {
                        const currentPOV = cameraController.isPOVModeEnabled();
                        this.togglePOVMode(!currentPOV);
                    }
                }
                break;
            case 'KeyE':
                // Cycle through trail types
                event.preventDefault();
                this.cycleTrailType();
                break;
            case 'KeyI':
                // Show scientific information
                event.preventDefault();
                this.showScientificInfo();
                break;
            case 'KeyA': {
                // Toggle asteroid belt
                event.preventDefault();
                const asteroidToggle = document.getElementById('asteroid-belt-toggle');
                if (asteroidToggle) {
                    asteroidToggle.checked = !asteroidToggle.checked;
                    this.toggleAsteroidBelt(asteroidToggle.checked);
                }
                break;
            }
            case 'KeyK': {
                // Toggle Kuiper belt
                event.preventDefault();
                const kuiperToggle = document.getElementById('kuiper-belt-toggle');
                if (kuiperToggle) {
                    kuiperToggle.checked = !kuiperToggle.checked;
                    this.toggleKuiperBelt(kuiperToggle.checked);
                }
                break;
            }
        }
    }

    /**
     * Toggle play/pause state
     */
    togglePlayPause() {
        if (!this.simulationEngine) return;

        const playPauseBtn = document.getElementById('play-pause-btn');
        const btnIcon = playPauseBtn?.querySelector('.btn-icon');
        const btnText = playPauseBtn?.querySelector('.btn-text');

        if (this.isPaused) {
            this.simulationEngine.resume();
            this.isPaused = false;
            if (btnIcon) btnIcon.innerHTML = svgIcon('pause');
            if (btnText) btnText.textContent = 'Pausar';
            playPauseBtn?.classList.remove('paused');
        } else {
            this.simulationEngine.pause();
            this.isPaused = true;
            if (btnIcon) btnIcon.innerHTML = svgIcon('play');
            if (btnText) btnText.textContent = 'Reproducir';
            playPauseBtn?.classList.add('paused');
        }

        // Update mobile display if it exists
        if (this.isMobile) {
            this.updateMobileSpeedDisplay();
        }

        this.updateSimulationStatus();
    }

    /**
     * Reset the simulation to initial state
     */
    resetSimulation() {
        if (!this.simulationEngine) return;

        this.simulationEngine.reset();
        this.renderer3D?.clearTrails();

        // Reset UI state
        this.isPaused = false;

        // Update controls
        const speedSlider = document.getElementById('speed-slider');
        if (speedSlider) speedSlider.value = '1'; // Muy Lento speed index

        // Set speed to match UI (this will update currentSpeed and apply to simulation engine)
        this.setSpeed(1); // Index 1 = "Muy Lento" with value 0.1

        const playPauseBtn = document.getElementById('play-pause-btn');
        const btnIcon = playPauseBtn?.querySelector('.btn-icon');
        const btnText = playPauseBtn?.querySelector('.btn-text');
        if (btnIcon) btnIcon.innerHTML = svgIcon('pause');
        if (btnText) btnText.textContent = 'Pausar';
        playPauseBtn?.classList.remove('paused');

        this.updateUI();
    }

    /**
     * Set simulation speed
     * @param {number} speedIndex - Index in speed presets array
     */
    setSpeed(speedIndex) {
        if (!this.simulationEngine || speedIndex < 0 || speedIndex >= this.speedPresets.length)
            return;

        const preset = this.speedPresets[speedIndex];
        this.simulationEngine.setTimeScale(preset.value);
        this.currentSpeed = preset.value;

        const speedDisplay = document.getElementById('speed-display');
        if (speedDisplay) {
            const speedText =
                preset.value === 0
                    ? `${preset.label} (0 años/s)`
                    : `${preset.label} (${preset.value} año${preset.value === 1 ? '' : 's'}/s)`;
            speedDisplay.textContent = speedText;
        }

        // Update mobile display if it exists
        if (this.isMobile) {
            this.updateMobileSpeedDisplay();
        }

        console.log(`Speed set to: ${preset.label} (${preset.value}x)`);
    }

    /**
     * Adjust speed by increment/decrement
     * @param {number} direction - 1 for increase, -1 for decrease
     */
    adjustSpeed(direction) {
        const speedSlider = document.getElementById('speed-slider');
        if (!speedSlider) return;

        const currentIndex = parseInt(speedSlider.value);
        const newIndex = Math.max(
            0,
            Math.min(this.speedPresets.length - 1, currentIndex + direction)
        );

        speedSlider.value = newIndex.toString();
        this.setSpeed(newIndex);
    }

    /**
     * Toggle trails visibility
     * @param {boolean} visible - Optional explicit visibility state
     */
    toggleTrails(visible = null) {
        if (!this.renderer3D) return;

        if (visible === null) {
            this.trailsVisible = !this.trailsVisible;
        } else {
            this.trailsVisible = visible;
        }

        this.renderer3D.setTrailsVisible(this.trailsVisible);

        const trailsToggle = document.getElementById('trails-toggle');
        if (trailsToggle && visible === null) {
            trailsToggle.checked = this.trailsVisible;
        }

        console.log(`Trails ${this.trailsVisible ? 'enabled' : 'disabled'}`);
    }

    /**
     * Clear all trails
     */
    clearTrails() {
        if (!this.renderer3D) return;

        this.renderer3D.clearTrails();
        console.log('Trails cleared');
    }

    /**
     * Set the trail type for all trails
     * @param {string} trailType - Type of trail to use
     */
    setTrailType(trailType) {
        if (!this.renderer3D) return;

        const trailSystem = this.renderer3D.getTrailSystem();
        if (trailSystem && typeof trailSystem.setTrailType === 'function') {
            trailSystem.setTrailType(trailType);

            // Update UI to reflect the change
            const trailTypeSelect = document.getElementById('trail-type-select');
            if (trailTypeSelect) {
                trailTypeSelect.value = trailType;
            }

            console.log(`Trail type changed to: ${trailType}`);
        }
    }

    /**
     * Set trail length mode
     * @param {string} mode - 'fixed' or 'proportional'
     */
    setTrailLengthMode(mode) {
        if (!this.renderer3D) return;

        const trailSystem = this.renderer3D.getTrailSystem();
        if (trailSystem && typeof trailSystem.setTrailLengthMode === 'function') {
            trailSystem.setTrailLengthMode(mode);

            // Update UI to reflect the change
            const trailLengthModeSelect = document.getElementById('trail-length-mode');
            if (trailLengthModeSelect) {
                trailLengthModeSelect.value = mode;
            }

            console.log(`Trail length mode changed to: ${mode}`);
        }
    }

    /**
     * Set trail length multiplier
     * @param {number} multiplier - Multiplier value (0.1 to 3.0)
     */
    setTrailLengthMultiplier(multiplier) {
        if (!this.renderer3D) return;

        const trailSystem = this.renderer3D.getTrailSystem();
        if (trailSystem && typeof trailSystem.setTrailLengthMultiplier === 'function') {
            trailSystem.setTrailLengthMultiplier(multiplier);

            // Update display
            const trailLengthDisplay = document.getElementById('trail-length-display');
            if (trailLengthDisplay) {
                trailLengthDisplay.textContent = `${multiplier.toFixed(1)}x`;
            }

            console.log(`Trail length multiplier changed to: ${multiplier}`);
        }
    }

    /**
     * Toggle staggered trail initialization
     * @param {boolean} enabled - Whether staggered trails should be enabled
     */
    toggleStaggeredTrails(enabled) {
        if (!this.renderer3D) return;

        const trailSystem = this.renderer3D.getTrailSystem();
        if (trailSystem && typeof trailSystem.setStaggeredInitialization === 'function') {
            trailSystem.setStaggeredInitialization(enabled);
            console.log(`Staggered trails ${enabled ? 'enabled' : 'disabled'}`);
        }
    }

    /**
     * Set staggered trail delay
     * @param {number} delay - Delay in milliseconds
     */
    setStaggeredDelay(delay) {
        if (!this.renderer3D) return;

        const trailSystem = this.renderer3D.getTrailSystem();
        if (trailSystem && typeof trailSystem.setStaggeredDelay === 'function') {
            trailSystem.setStaggeredDelay(delay);

            // Update display
            const staggeredDelayDisplay = document.getElementById('staggered-delay-display');
            if (staggeredDelayDisplay) {
                staggeredDelayDisplay.textContent = `${(delay / 1000).toFixed(1)}s`;
            }

            console.log(`Staggered delay changed to: ${delay}ms`);
        }
    }

    /**
     * Reset staggered trail initialization
     */
    resetStaggeredTrails() {
        if (!this.renderer3D) return;

        const trailSystem = this.renderer3D.getTrailSystem();
        if (trailSystem && typeof trailSystem.resetStaggeredInitialization === 'function') {
            trailSystem.resetStaggeredInitialization();
            console.log('Staggered trails reset');
        }
    }

    /**
     * Fix problematic planet trails that are not showing
     */
    fixOddTrails() {
        if (!this.renderer3D) return;

        const trailSystem = this.renderer3D.getTrailSystem();
        if (trailSystem && typeof trailSystem.forceFixOddTrails === 'function') {
            trailSystem.forceFixOddTrails();
            console.log('Problematic trails fixed');
        }
    }

    /**
     * Force update problematic trails
     */
    forceUpdateTrails() {
        if (!this.renderer3D) return;

        const trailSystem = this.renderer3D.getTrailSystem();
        if (trailSystem && typeof trailSystem.forceUpdateProblematicTrails === 'function') {
            trailSystem.forceUpdateProblematicTrails();
            console.log('Problematic trails updated');
        }
    }

    /**
     * Toggle POV (Point of View) mode
     * @param {boolean} enabled - Whether POV mode should be enabled
     */
    togglePOVMode(enabled) {
        this.camera.togglePOVMode(enabled);
    }

    /**
     * Toggle star field visibility
     * @param {boolean} visible - Whether star field should be visible
     */
    toggleStarField(visible) {
        this.environment.toggleStarField(visible);
    }

    /**
     * Toggle star twinkling effect
     * @param {boolean} enabled - Whether twinkling should be enabled
     */
    toggleStarTwinkling(enabled) {
        this.environment.toggleStarTwinkling(enabled);
    }

    /**
     * Toggle galactic structure simulation
     * @param {boolean} enabled - Whether galactic structure should be enabled
     */
    toggleGalacticStructure(enabled) {
        this.environment.toggleGalacticStructure(enabled);
    }

    /**
     * Toggle asteroid belt visibility
     * @param {boolean} visible - Whether asteroid belt should be visible
     */
    toggleAsteroidBelt(visible) {
        this.environment.toggleAsteroidBelt(visible);
    }

    /**
     * Toggle Kuiper belt visibility
     * @param {boolean} visible - Whether Kuiper belt should be visible
     */
    toggleKuiperBelt(visible) {
        this.environment.toggleKuiperBelt(visible);
    }

    /**
     * Toggle heliosphere visibility
     * @param {boolean} visible - Whether heliosphere should be visible
     */
    toggleHeliosphere(visible) {
        this.environment.toggleHeliosphere(visible);
    }

    /**
     * Show scientific information modal (delega en InfoModalControls)
     */
    showScientificInfo() {
        this.infoModal.showScientificInfo();
    }

    /**
     * Cycle through available trail types
     */
    cycleTrailType() {
        if (!this.renderer3D) return;

        const trailSystem = this.renderer3D.getTrailSystem();
        if (!trailSystem || typeof trailSystem.getAvailableTrailTypes !== 'function') return;

        const availableTypes = Object.keys(trailSystem.getAvailableTrailTypes());
        const currentType = trailSystem.getTrailType();
        const currentIndex = availableTypes.indexOf(currentType);
        const nextIndex = (currentIndex + 1) % availableTypes.length;
        const nextType = availableTypes[nextIndex];

        this.setTrailType(nextType);

        // Show notification
        const trailTypes = trailSystem.getAvailableTrailTypes();
        const typeName = trailTypes[nextType]?.name || nextType;
        this.showTrailTypeNotification(typeName, trailTypes[nextType]?.icon || '');
    }

    /**
     * Show a notification when trail type changes
     * @param {string} typeName - Name of the trail type
     * @param {string} icon - Icon for the trail type
     */
    showTrailTypeNotification(typeName, icon) {
        // Remove existing notification
        const existingNotification = document.querySelector('.trail-type-notification');
        if (existingNotification) {
            existingNotification.remove();
        }

        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'trail-type-notification';
        notification.innerHTML = `
            <span class="trail-icon">${icon}</span>
            <span class="trail-name">${typeName}</span>
        `;

        // Add to body
        document.body.appendChild(notification);

        // Remove after 2 seconds
        setTimeout(() => {
            if (notification.parentNode) {
                notification.remove();
            }
        }, 2000);
    }

    /**
     * Toggle camera tracking on/off
     * @param {boolean} enabled - Whether tracking should be enabled
     */
    toggleCameraTracking(enabled) {
        this.camera.toggleCameraTracking(enabled);
    }

    /**
     * Aplica un preset de cámara (vista predefinida) delegando en el renderer, y refresca
     * la UI de seguimiento (algunos presets activan/desactivan el tracking).
     * @param {string} preset - 'top' | 'front' | 'earth45' | 'overview'
     */
    applyCameraPreset(preset) {
        if (!this.renderer3D || typeof this.renderer3D.applyCameraPreset !== 'function') return;

        // Este preset activa además su estructura, para que se vea nada más pulsar.
        if (preset === 'heliosphere') this.toggleHeliosphere(true);

        this.renderer3D.applyCameraPreset(preset);
        this.updateTrackingUI();
    }

    /**
     * Start tracking the sun
     */
    trackSun() {
        this.camera.trackSun();
    }

    /**
     * Start tracking a specific planet
     * @param {string} planetId - ID of the planet to track
     */
    trackPlanet(planetId) {
        this.camera.trackPlanet(planetId);
    }

    /**
     * Update tracking UI elements
     */
    updateTrackingUI() {
        this.camera.updateTrackingUI();
    }

    /**
     * Set tracking distance for camera
     * @param {number} distance - New tracking distance
     */
    setTrackingDistance(distance) {
        this.camera.setTrackingDistance(distance);
    }

    /**
     * Get planet display name from ID
     * @param {string} planetId - Planet ID
     * @returns {string} Planet display name
     */
    getPlanetName(planetId) {
        return this.camera.getPlanetName(planetId);
    }

    /**
     * Update the realistic size and orbital ratio displays in the UI
     */
    updateSizeRatioDisplay() {
        const sizeRatioDisplay = document.getElementById('size-ratio-display');
        const jupiterRatioDisplay = document.getElementById('jupiter-ratio-display');
        const neptuneOrbitDisplay = document.getElementById('neptune-orbit-display');
        const jupiterOrbitDisplay = document.getElementById('jupiter-orbit-display');

        try {
            import('./solarSystemData.js').then((module) => {
                const ratios = module.getRealSizeRatios();
                const solarSystemData = module.solarSystemData;
                const earthOrbitalRadius = 149.598e6; // km

                // Size ratios
                if (ratios && ratios.earth && sizeRatioDisplay) {
                    sizeRatioDisplay.textContent = `${ratios.earth.realRatio.toFixed(1)}x`;
                    sizeRatioDisplay.className = 'status-value fps-good';
                }

                if (ratios && ratios.jupiter && jupiterRatioDisplay) {
                    jupiterRatioDisplay.textContent = `${ratios.jupiter.realRatio.toFixed(1)}x`;
                    jupiterRatioDisplay.className = 'status-value fps-good';
                }

                // Orbital ratios
                if (neptuneOrbitDisplay) {
                    const neptune = solarSystemData.planets.find((p) => p.id === 'neptune');
                    const neptuneOrbitRatio = neptune.orbitalRadius / earthOrbitalRadius;
                    neptuneOrbitDisplay.textContent = `${neptuneOrbitRatio.toFixed(1)}x`;
                    neptuneOrbitDisplay.className = 'status-value fps-good';
                }

                if (jupiterOrbitDisplay) {
                    const jupiter = solarSystemData.planets.find((p) => p.id === 'jupiter');
                    const jupiterOrbitRatio = jupiter.orbitalRadius / earthOrbitalRadius;
                    jupiterOrbitDisplay.textContent = `${jupiterOrbitRatio.toFixed(1)}x`;
                    jupiterOrbitDisplay.className = 'status-value fps-good';
                }
            });
        } catch (error) {
            console.error('Error updating ratio displays:', error);
        }
    }

    /**
     * Initialize realistic proportions UI state
     */
    initializeSizingModeUI() {
        // Update the size ratio displays with real values
        this.updateSizeRatioDisplay();
        console.log('🔬 Proporciones reales inicializadas - Sol es 109x más grande que la Tierra');
    }

    /**
     * Update all UI elements with current state
     */
    updateUI() {
        this.updateSimulationStatus();
        this.updateSimulationTime();
        this.updateFPS();
        this.updateTrackingUI();
        this.updateSizeRatioDisplay();
    }

    /**
     * Update simulation status display
     */
    updateSimulationStatus() {
        const statusElement = document.getElementById('simulation-status');
        if (!statusElement) return;

        if (this.isPaused) {
            statusElement.textContent = 'Pausado';
            statusElement.className = 'status-value paused';
        } else {
            statusElement.textContent = 'Ejecutándose';
            statusElement.className = 'status-value running';
        }
    }

    /**
     * Update simulation time display
     */
    updateSimulationTime() {
        const timeElement = document.getElementById('simulation-time');
        if (!timeElement || !this.simulationEngine) return;

        const state = this.simulationEngine.getSimulationState();
        const days = Math.floor(state.currentTime);
        const years = Math.floor(days / 365.25);

        let timeText;
        if (years > 0) {
            timeText = `${years} años, ${days % 365} días`;
        } else {
            timeText = `${days} días`;
        }

        timeElement.textContent = timeText;
    }

    /**
     * Update FPS display
     */
    updateFPS() {
        const fpsElement = document.getElementById('fps-display');
        if (!fpsElement || !this.renderer3D) return;

        const fps = this.renderer3D.getFPS ? this.renderer3D.getFPS() : 60;
        fpsElement.textContent = Math.round(fps);

        // Color code FPS display
        if (fps < 20) {
            fpsElement.className = 'status-value fps-low';
        } else if (fps < 40) {
            fpsElement.className = 'status-value fps-medium';
        } else {
            fpsElement.className = 'status-value fps-good';
        }
    }

    /**
     * Start periodic UI updates
     */
    startPeriodicUpdates() {
        // Update time and FPS every second.
        // Guardamos el id para poder cancelarlo en dispose() y evitar timers huérfanos
        // que se acumulan en cada ciclo de pérdida/recuperación de contexto WebGL.
        if (this.updateIntervalId !== null) {
            clearInterval(this.updateIntervalId);
        }
        this.updateIntervalId = setInterval(() => {
            if (this.isInitialized) {
                this.updateSimulationTime();
                this.updateFPS();
            }
        }, 1000);
    }

    /**
     * Detiene las actualizaciones periódicas y libera recursos.
     * Debe llamarse al descargar la página o antes de reinicializar la UI.
     */
    dispose() {
        if (this.updateIntervalId !== null) {
            clearInterval(this.updateIntervalId);
            this.updateIntervalId = null;
        }
        this.isInitialized = false;
    }

    /**
     * Show loading indicator
     */
    showLoadingIndicator() {
        const loadingIndicator = document.getElementById('loading-indicator');
        if (loadingIndicator) {
            loadingIndicator.classList.remove('hidden');
        }
    }

    /**
     * Hide loading indicator
     */
    hideLoadingIndicator() {
        const loadingIndicator = document.getElementById('loading-indicator');
        if (loadingIndicator) {
            loadingIndicator.classList.add('hidden');
        }
    }

    /**
     * Handle and display errors
     * @param {string} title - Error title
     * @param {string} message - Error message
     */
    handleError(title, message) {
        const errorContainer = document.getElementById('error-container');
        if (!errorContainer) return;

        const errorDiv = document.createElement('div');
        errorDiv.className = 'error-message';
        errorDiv.innerHTML = `
            <h4>${title}</h4>
            <p>${message}</p>
            <button onclick="this.parentElement.remove()" class="error-close">×</button>
        `;

        errorContainer.appendChild(errorDiv);

        // Auto-remove after 10 seconds
        setTimeout(() => {
            if (errorDiv.parentElement) {
                errorDiv.remove();
            }
        }, 10000);
    }

    /**
     * Setup UI toggle controls for showing/hiding the overlay
     */
    setupUIToggleControls() {
        const toggleBtn = document.getElementById('ui-toggle-btn');
        const closeBtn = document.getElementById('ui-close-btn');
        const overlay = document.getElementById('ui-overlay');

        // Toggle button click
        toggleBtn?.addEventListener('click', () => {
            this.toggleUIVisibility();
        });

        // Close button click
        closeBtn?.addEventListener('click', () => {
            this.hideUI();
        });

        // ESC key to hide UI
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.uiVisible) {
                this.hideUI();
            }
        });

        console.log('UI toggle controls setup complete');
    }

    /**
     * Setup mouse wheel control for camera distance slider
     */
    setupMouseWheelControl() {
        const canvas = document.getElementById('webgl-canvas');
        const distanceSlider = document.getElementById('tracking-distance-slider');

        if (!canvas || !distanceSlider) {
            console.warn('Canvas or distance slider not found for mouse wheel control');
            return;
        }

        canvas.addEventListener(
            'wheel',
            (e) => {
                // Only control distance if tracking is enabled
                const trackingToggle = document.getElementById('tracking-toggle');
                if (!trackingToggle || !trackingToggle.checked) {
                    return;
                }

                e.preventDefault();

                const currentDistance = parseInt(distanceSlider.value);
                const minDistance = parseInt(distanceSlider.min);
                const maxDistance = parseInt(distanceSlider.max);

                // Calculate distance change based on wheel delta
                // Positive deltaY = wheel down = zoom out (increase distance)
                // Negative deltaY = wheel up = zoom in (decrease distance)
                const deltaFactor = e.deltaY > 0 ? 1.1 : 0.9;
                let newDistance = Math.round(currentDistance * deltaFactor);

                // Clamp to slider limits
                newDistance = Math.max(minDistance, Math.min(maxDistance, newDistance));

                // Update slider and distance
                if (newDistance !== currentDistance) {
                    distanceSlider.value = newDistance;
                    this.setTrackingDistance(newDistance);
                }
            },
            { passive: false }
        );

        console.log('Mouse wheel control for camera distance setup complete');
    }

    /**
     * Toggle UI overlay visibility
     */
    toggleUIVisibility() {
        if (this.uiVisible) {
            this.hideUI();
        } else {
            this.showUI();
        }
    }

    /**
     * Show the UI overlay
     */
    showUI() {
        const overlay = document.getElementById('ui-overlay');
        const toggleBtn = document.getElementById('ui-toggle-btn');

        if (overlay) {
            overlay.classList.remove('collapsed');
            overlay.classList.remove('hidden');
        }

        if (toggleBtn) {
            toggleBtn.classList.add('hidden');
        }

        this.uiVisible = true;
        console.log('UI overlay shown');
    }

    /**
     * Hide the UI overlay
     */
    hideUI() {
        const overlay = document.getElementById('ui-overlay');
        const toggleBtn = document.getElementById('ui-toggle-btn');

        if (overlay) {
            overlay.classList.add('collapsed');
        }

        if (toggleBtn) {
            toggleBtn.classList.remove('hidden');
        }

        this.uiVisible = false;
        console.log('UI overlay hidden');
    }

    /**
     * Get current UI state
     * @returns {Object} Current UI state
     */
    getUIState() {
        return {
            visible: this.uiVisible,
            trailsVisible: this.trailsVisible,
            currentSpeed: this.currentSpeed,
            isPaused: this.isPaused,
            isInitialized: this.isInitialized,
        };
    }

    // --- Móvil: delegan en MobileControls ---

    detectMobile() {
        return this.mobile.detectMobile();
    }

    createMobileSpeedControls() {
        this.mobile.createMobileSpeedControls();
    }

    removeMobileSpeedControls() {
        this.mobile.removeMobileSpeedControls();
    }

    updateMobileSpeedDisplay() {
        this.mobile.updateMobileSpeedDisplay();
    }
}
